import {
  Autocomplete,
  Box,
  FilterOptionsState,
  FormHelperText,
  Tooltip,
  type PopperProps,
} from '@mui/material';
import { OpenCreateLevelDialog } from 'components/dataCreationForms/level/types';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { OpenCreateDialog } from 'components/dataCreationForms/types';
import { Disabled } from 'components/dataCreationForms/withInputForm/statefulInputsWrapper/types';
import React, {
  ReactElement,
  Ref,
  SyntheticEvent,
  forwardRef,
  memo,
  useCallback,
} from 'react';
import { useIntl } from 'react-intl';
import { Color } from 'shared/utils/colors';
import {
  EditableFieldValuesEntityName,
  EditableStandardEntityName,
} from 'shared/domain/fieldValue/fields';
import {
  Identificable,
  Labelled,
  LabelledEntity,
} from 'shared/types/commonView';
import {
  formHasSiteField,
  getSiteIdFromValues,
  getAutocompleteRenderInput,
  getDisableCloseOnSelect,
  getFreeSoloDialogAdditionalParams,
  getOnMultipleFreeSoloChangeHandler,
  getPreselectedMapFromForm,
  getSiteIdFromForm,
  isOptionEqualToValue,
  multipleFreeSoloFilterOptions,
  PopupControlStrategy,
  unifiedGetOptionKey,
  unifiedGetOptionLabel,
} from '../common';
import {
  FormValuesGetter,
  FreeSoloMultiAutocompleteValue,
} from '../types';
import { OverflowChipTags } from './components/overflowChipTags';
import { NotificationTranslationKeys } from 'intl/keys';
import { FieldTranslationKeys } from 'intl/keys';

interface MultiAutocompleteProps {
  available: LabelledEntity[];
  groupBy?: (option: LabelledEntity) => string;
  localisedLabel: string;
  required: boolean;
  disabled?: Disabled;
  handleChange: (_: any, value: FreeSoloMultiAutocompleteValue[]) => void;
  error?: string;
  ref?: Ref<HTMLDivElement>;
  value: LabelledEntity[] | null;
  id: string;
  disableClearable?: boolean;
  helperText?: string;
  dense: boolean;
  reserveSpaceForHelperText: boolean;
  getOptionLabel?: (option: any) => string;
  DialogComponent?: ReactElement;
  useProvider?: () => {
    openDialog: OpenCreateDialog | OpenCreateLevelDialog;
  };
  entityName?:
    | string
    | EditableStandardEntityName
    | EditableFieldValuesEntityName;
  formValuesGetter?: FormValuesGetter;
  popupControlStrategy?: PopupControlStrategy;
  size?: 'small' | 'medium';
  maxVisibleTags?: number;
  /** Focused with its list always open below the field, in the same panel (e.g. the only control of a popover). */
  openOnMount?: boolean;
}

/** The list of the Autocomplete as a block under its field, not a floating popper. */
const InlineListPopper = ({ children }: PopperProps): React.ReactElement => (
  <Box sx={{ width: '100%' }}>{typeof children === 'function' ? children({ placement: 'bottom' }) : children}</Box>
);

const MultiAutocomplete = (
  {
    available,
    groupBy,
    value,
    handleChange,
    disabled,
    helperText,
    id,
    required,
    localisedLabel,
    error,
    disableClearable,
    useProvider,
    DialogComponent,
    dense,
    reserveSpaceForHelperText,
    entityName,
    formValuesGetter,
    popupControlStrategy,
    size,
    maxVisibleTags = 2,
    openOnMount = false,
  }: MultiAutocompleteProps,
  ref: Ref<HTMLDivElement>
): React.ReactElement => {
  const intl = useIntl();
  const multiAutocompleteStandardHandleChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      values: FreeSoloMultiAutocompleteValue[]
    ) => {
      handleChange(event, values);
    },
    [handleChange]
  );
  let onChange = multiAutocompleteStandardHandleChange;
  let filterOptions;
  let freeSolo = false;
  let siteId = '';
  // TODO: very nasty workaround until we disable fields properly on form level based on dependsOn values
  // we don't want it on use documentation as map screen
  const formValues = formValuesGetter?.().values;
  const companyNeedsSite =
    entityName === EditableStandardEntityName.company &&
    formValues != null &&
    formHasSiteField(formValues);
  if (
    (entityName === EditableStandardEntityName.level || companyNeedsSite) &&
    !window.location.href.includes('documentation')
  ) {
    siteId =
      formValues != null
        ? getSiteIdFromValues(formValues)
        : getSiteIdFromForm(formValuesGetter);
    if (!siteId) {
      disabled = true;
      helperText = formatTranslation(intl, NotificationTranslationKeys.issueFormDependedNotFilled , { fieldLabel: formatTranslation(intl, FieldTranslationKeys.site ) }
      );
    }
  }
  if (useProvider) {
    const { openDialog } = useProvider();
    freeSolo = true;
    filterOptions = useCallback(
      (
        options: FreeSoloMultiAutocompleteValue[],
        params: FilterOptionsState<
          Identificable<string> &
            Labelled & { inputValue?: string | undefined }  
        >
      ) => multipleFreeSoloFilterOptions(options, params, intl),
      [intl]
    );
    const additionalParams = {
      ...getFreeSoloDialogAdditionalParams(siteId),
      ...getPreselectedMapFromForm(formValuesGetter),
    };

    onChange = getOnMultipleFreeSoloChangeHandler(
      openDialog,
      handleChange,
      additionalParams
    );
  }

  const inputRenderer = getAutocompleteRenderInput({
    autoFocus: openOnMount,
    required,
    disabled: Boolean(disabled),
    error,
    label: localisedLabel,
    id: `${id}-textField`,
    placeholder: '',
    dense,
  });

  const disableCloseOnSelect = popupControlStrategy
    ? getDisableCloseOnSelect(popupControlStrategy, value?.length ?? 0)
    : true;

  const renderTags = useCallback(
    (
      selectedValues: FreeSoloMultiAutocompleteValue[],
      getTagProps: (args: { index: number }) => Record<string, unknown>
    ) => (
      <OverflowChipTags
        selectedValues={selectedValues}
        getTagProps={getTagProps}
        getOptionKey={unifiedGetOptionKey}
        getOptionLabel={unifiedGetOptionLabel}
        maxVisibleTags={maxVisibleTags}
        size={size}
      />
    ),
    [maxVisibleTags, size]
  );

  return (
    <Tooltip
      title={
        typeof disabled === 'object'
          ? formatTranslation(intl, unsafeTranslationKey(disabled.reason) )
          : undefined
      }
    >
      <Box>
        <Autocomplete
          options={available}
          // NOTE: When using groupBy in Autocomplete, the options MUST be sorted
          // by the same property you group by (here: group).
          // Otherwise, group headers may be duplicated or appear in the wrong places.
          groupBy={groupBy}
          multiple
          disableCloseOnSelect={disableCloseOnSelect}
          {...(openOnMount ? { open: !disabled, PopperComponent: InlineListPopper } : {})}
          limitTags={maxVisibleTags}
          disabled={Boolean(disabled)}
          value={value || []}
          id={`${id}-multiAutocomplete`}
          isOptionEqualToValue={isOptionEqualToValue}
          ref={ref}
          disableClearable={!!disableClearable}
          getOptionLabel={unifiedGetOptionLabel}
          renderInput={inputRenderer}
          getOptionKey={unifiedGetOptionKey}
          freeSolo={freeSolo}
          clearOnBlur={true}
          // @ts-ignore
          // option added after adding and hitting enter directly in input is indeed string
          // (when reason is "createOption", not "selectOption").
          // "selectOption" reason occurs when "Add something is clicked")
          onChange={onChange}
          renderTags={renderTags}
          filterOptions={filterOptions}
          size={size}
          slotProps={{
            popper: {
              sx: {
                '& .MuiAutocomplete-groupLabel': {
                  color: Color.blue,
                },
              },
            },
          }}
        />
        {DialogComponent}
        <FormHelperText error={!!error}>
          {helperText || (reserveSpaceForHelperText && ' ')}
        </FormHelperText>
      </Box>
    </Tooltip>
  );
};

const ForwardedMultiAutoComplete = forwardRef<
  HTMLDivElement,
  MultiAutocompleteProps
>(
  MultiAutocomplete
) as React.ForwardRefExoticComponent<MultiAutocompleteProps>;

export const MemoMultiAutocomplete = memo(ForwardedMultiAutoComplete);
